import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import CKEditorWidget from '../../../../components/widgets/CKEditorWidget';
import { fetchLatestTermsPublic } from '../../../../assets/lib/api/termsApi';
import { sanitizeHtml } from '../../../../assets/utils/comm/sanitize';

/** 본문·에디터 영역 끝까지 스크롤했는지 판별할 여유(px) */
const SCROLL_END_EPS = 12;

/**
 * 회원가입 등 — 이용약관·개인정보처리방침을 공통 팝업에서 CKEditor 읽기 전용으로 표시
 * config.termsDstnCd: USER_TERMS | USER_PRIVATE
 * config.title: 팝업 제목
 * 스크롤을 끝까지 내리면 하단 버튼이 '확인'으로 바뀌며, 클릭 시 onClose(true, { agreed: true })로 닫힘
 */
const LegalTermsViewerPopup = ({ config, onClose }) => {
  const { t, i18n } = useTranslation();
  const termsDstnCd = config?.termsDstnCd;
  const reactId = useId().replace(/:/g, '');
  const editorId = `legal-terms-ck-${termsDstnCd || 'x'}-${reactId}`;
  const bodyRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [html, setHtml] = useState('');
  const [termsUpdtDt, setTermsUpdtDt] = useState('');
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  useEffect(() => {
    if (!termsDstnCd) {
      setLoading(false);
      setError(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    setHtml('');
    setTermsUpdtDt('');
    (async () => {
      try {
        const data = await fetchLatestTermsPublic(termsDstnCd, i18n.language);
        if (cancelled) return;
        if (!data) {
          setTermsUpdtDt('');
          setHtml('');
          return;
        }
        setTermsUpdtDt(data.termsUpdtDt || '');
        setHtml(typeof data.termsCnt === 'string' ? data.termsCnt : '');
      } catch {
        if (!cancelled) {
          setError(true);
          setHtml('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [termsDstnCd, i18n.language]);

  const updateScrolledToEnd = useCallback(() => {
    const root = bodyRef.current;
    if (!root) return;
    const outerShort = root.scrollHeight <= root.clientHeight + SCROLL_END_EPS;
    const outerAtEnd =
      outerShort || root.scrollTop + root.clientHeight >= root.scrollHeight - SCROLL_END_EPS;
    const inner = root.querySelector('.ck.ck-editor__main > .ck-editor__editable');
    let innerAtEnd = true;
    if (inner && inner.scrollHeight > inner.clientHeight + SCROLL_END_EPS) {
      innerAtEnd = inner.scrollTop + inner.clientHeight >= inner.scrollHeight - SCROLL_END_EPS;
    }
    setScrolledToEnd(outerAtEnd && innerAtEnd);
  }, []);

  useEffect(() => {
    if (!html?.trim() || loading || error) {
      setScrolledToEnd(false);
      return undefined;
    }
    const root = bodyRef.current;
    if (!root) return undefined;

    let innerEl = null;
    const onScrollOrResize = () => updateScrolledToEnd();

    const bindInner = () => {
      const next = root.querySelector('.ck.ck-editor__main > .ck-editor__editable');
      if (next === innerEl) return;
      if (innerEl) innerEl.removeEventListener('scroll', onScrollOrResize);
      innerEl = next;
      if (innerEl) innerEl.addEventListener('scroll', onScrollOrResize, { passive: true });
    };

    bindInner();
    const mo = new MutationObserver(() => {
      bindInner();
      updateScrolledToEnd();
    });
    mo.observe(root, { childList: true, subtree: true });

    root.addEventListener('scroll', onScrollOrResize, { passive: true });
    const ro = new ResizeObserver(onScrollOrResize);
    ro.observe(root);

    const timeouts = [80, 300, 800, 1600].map((ms) => window.setTimeout(updateScrolledToEnd, ms));
    updateScrolledToEnd();

    return () => {
      mo.disconnect();
      ro.disconnect();
      root.removeEventListener('scroll', onScrollOrResize);
      if (innerEl) innerEl.removeEventListener('scroll', onScrollOrResize);
      timeouts.forEach(clearTimeout);
    };
  }, [html, loading, error, updateScrolledToEnd]);

  const safeHtml = html?.trim() ? sanitizeHtml(html) : '';

  const handleRightButtonClick = () => {
    if (loading || error || !safeHtml) {
      onClose(false);
      return;
    }
    if (scrolledToEnd) {
      onClose(true, { agreed: true });
    } else {
      onClose(false);
    }
  };

  const closeLabel = config?.rightButton?.text || t('popup.common.close');
  const rightButtonLabel =
    loading || error || !safeHtml ? closeLabel : scrolledToEnd ? t('popup.common.confirm') : closeLabel;

  return (
    <PopupWrapper
      config={config}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-legal-terms-viewer"
    >
      <div ref={bodyRef} className="main-popup-body popup-legal-terms-viewer-body">
        {loading && <p className="text-muted mb-0 px-2">{t('popup.legalTermsViewer.loading')}</p>}
        {!loading && error && (
          <p className="text-danger mb-0 px-2">{t('popup.legalTermsViewer.loadError')}</p>
        )}
        {!loading && !error && !safeHtml && (
          <p className="text-muted mb-0 px-2">{t('popup.legalTermsViewer.empty')}</p>
        )}
        {!loading && !error && safeHtml ? (
          <>
            {termsUpdtDt ? (
              <p className="small text-secondary mb-2 px-2">
                {t('popup.legalTermsViewer.effectiveDate')}: <strong className="text-body">{termsUpdtDt}</strong>
              </p>
            ) : null}
            <div className="popup-legal-terms-ckeditor-wrap px-2">
              <CKEditorWidget
                data={{
                  id: editorId,
                  content: safeHtml,
                  editorHeight: Math.min(480, typeof window !== 'undefined' ? Math.floor(window.innerHeight * 0.5) : 400),
                  readOnly: true,
                  hideToolbar: true,
                }}
              />
            </div>
          </>
        ) : null}
      </div>
      <div
        className={`main-popup-footer popup-legal-terms-viewer-footer d-flex align-items-center gap-3 flex-wrap ${
          !loading && !error && safeHtml ? 'justify-content-between' : 'justify-content-end'
        }`}
      >
        {!loading && !error && safeHtml ? (
          <div
            className={`popup-legal-terms-scroll-hint ${scrolledToEnd ? 'popup-legal-terms-scroll-hint--done' : ''}`}
            role="status"
            aria-live="polite"
          >
            <span className="popup-legal-terms-scroll-hint__icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 5v14M12 19l-4-4m4 4l4-4"
                  stroke="currentColor"
                  strokeWidth="1.85"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <div className="popup-legal-terms-scroll-hint__textwrap">
              {scrolledToEnd ? (
                <>
                  <span className="popup-legal-terms-scroll-hint__label">{t('popup.legalTermsViewer.readyLabel')}</span>
                  <p
                    className="popup-legal-terms-scroll-hint__msg mb-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('popup.legalTermsViewer.readyMsg')) }}
                  />
                </>
              ) : (
                <>
                  <span className="popup-legal-terms-scroll-hint__label">{t('popup.legalTermsViewer.scrollHintLabel')}</span>
                  <p
                    className="popup-legal-terms-scroll-hint__msg mb-0"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('popup.legalTermsViewer.scrollHintMsg')) }}
                  />
                </>
              )}
            </div>
          </div>
        ) : null}
        {config?.rightButton ? (
          <div className="popup-legal-terms-viewer-footer__actions d-flex align-items-center flex-shrink-0">
            <button
              type="button"
              className={`btn main-popup-right-btn ${getButtonClass(config.rightButton.color || 'btn-primary')}`}
              onClick={handleRightButtonClick}
              title={
                !loading && !error && safeHtml && !scrolledToEnd
                  ? t('popup.legalTermsViewer.scrollToConfirmTitle')
                  : undefined
              }
            >
              {rightButtonLabel}
            </button>
          </div>
        ) : null}
      </div>
      <style>{`
        .popup-legal-terms-viewer-body {
          max-height: min(72vh, 640px);
          overflow-y: auto;
        }
        .popup-legal-terms-viewer-footer {
          padding-left: 15px;
          padding-right: 15px;
        }
        .popup-legal-terms-scroll-hint {
          flex: 1 1 220px;
          min-width: 0;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 10px 12px 10px 10px;
          border-radius: 10px;
          background: linear-gradient(135deg, #fff8f8 0%, #fff0f0 42%, #fef6f6 100%);
          border: 1px solid #f9d0d4;
          box-shadow: 0 1px 3px rgba(190, 24, 60, 0.08);
        }
        .popup-legal-terms-scroll-hint--done {
          background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 55%, #f8fafc 100%);
          border-color: #a7f3d0;
        }
        .popup-legal-terms-scroll-hint__icon {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(232, 93, 111, 0.16);
          color: #c5303f;
        }
        .popup-legal-terms-scroll-hint--done .popup-legal-terms-scroll-hint__icon {
          background: rgba(5, 150, 105, 0.12);
          color: #047857;
        }
        .popup-legal-terms-scroll-hint__textwrap {
          min-width: 0;
        }
        .popup-legal-terms-scroll-hint__label {
          display: inline-block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          color: #64748b;
          margin-bottom: 4px;
        }
        .popup-legal-terms-scroll-hint:not(.popup-legal-terms-scroll-hint--done) .popup-legal-terms-scroll-hint__label {
          color: #b34452;
        }
        .popup-legal-terms-scroll-hint--done .popup-legal-terms-scroll-hint__label {
          color: #047857;
        }
        .popup-legal-terms-scroll-hint__msg {
          font-size: 13px;
          line-height: 1.55;
          color: #334155;
        }
        .popup-legal-terms-scroll-hint__msg strong {
          color: #0f172a;
          font-weight: 600;
        }
        @media (max-width: 576px) {
          .popup-legal-terms-viewer-footer {
            flex-direction: column;
            align-items: stretch !important;
          }
          .popup-legal-terms-scroll-hint {
            flex-basis: auto;
          }
          .popup-legal-terms-viewer-footer__actions {
            justify-content: flex-end;
            width: 100%;
          }
        }
        .popup-legal-terms-ckeditor-wrap .ck-editor-widget-container {
          border: none;
          border-radius: 0;
          overflow: visible;
        }
        .popup-legal-terms-ckeditor-wrap .ck.ck-editor {
          border: none !important;
          box-shadow: none !important;
        }
        .popup-legal-terms-ckeditor-wrap .ck.ck-editor__main > .ck-editor__editable {
          border: none !important;
          box-shadow: none !important;
          overflow: visible !important;
        }
      `}</style>
    </PopupWrapper>
  );
};

export default LegalTermsViewerPopup;
